import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Radar, FileText, Sparkles, Loader2, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import PulseBadge from './PulseBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PRICE = {
  UK: { radar: '£9.99', report: '£14.99', bundle: '£19.99' },
  IN: { radar: '₹999', report: '₹1,499', bundle: '₹1,999' },
  CA: { radar: 'C$14.99', report: 'C$19.99', bundle: 'C$27.99' },
};

/**
 * SellerPulseBoostPanel — appears once per listing on Seller dashboard.
 * Two paid add-ons (one-time per listing):
 *   • Developments Radar (£9.99) — planning + new-builds within 1km
 *   • Pulse Boost Report (£14.99) — AI report on raising the score
 *   • Bundle (£19.99)
 */
const SellerPulseBoostPanel = ({ listing }) => {
  const [status, setStatus] = useState(null);
  const [loadingSku, setLoadingSku] = useState(null);
  const [radarOpen, setRadarOpen] = useState(false);
  const [radarData, setRadarData] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportMd, setReportMd] = useState(null);

  const country = (listing?.country || 'UK').toUpperCase();
  const prices = PRICE[country] || PRICE.UK;

  const fetchStatus = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const r = await axios.get(`${API_URL}/api/pulse/seller/${listing.id}/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(r.data);
    } catch (e) {
      // 404 just means listing not owned or not loaded
    }
  };

  useEffect(() => {
    if (listing?.id) fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing?.id]);

  const purchase = async (sku) => {
    const token = localStorage.getItem('token');
    if (!token) { toast.error('Please log in'); return; }
    setLoadingSku(sku);
    try {
      const r = await axios.post(`${API_URL}/api/pulse/seller/checkout`, {
        listing_id: listing.id,
        sku,
        origin_url: window.location.origin,
        country,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (r.data?.already_unlocked) {
        toast.success('Already unlocked — opening it now.');
        await fetchStatus();
      } else if (r.data?.url) {
        window.location.href = r.data.url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
    } finally {
      setLoadingSku(null);
    }
  };

  const openRadar = async () => {
    const token = localStorage.getItem('token');
    setRadarOpen(true);
    if (radarData) return;
    try {
      const r = await axios.get(`${API_URL}/api/pulse/seller/${listing.id}/radar`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRadarData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load radar');
    }
  };

  const openReport = async () => {
    const token = localStorage.getItem('token');
    setReportOpen(true);
    if (reportMd) return;
    try {
      const r = await axios.get(`${API_URL}/api/pulse/seller/${listing.id}/report`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReportMd(r.data?.markdown || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load report');
    }
  };

  const radarUnlocked = !!status?.developments_radar;
  const reportUnlocked = !!status?.boost_report;

  return (
    <div className="luxury-card p-5 sm:p-6 mt-4" data-testid={`seller-pulse-boost-${listing.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <span className="text-[10px] tracking-[0.3em] text-[#E07A3A] uppercase font-medium">Pulse Boost Pack</span>
          <h4 className="font-['Playfair_Display'] text-lg text-[#1A1A1A]">
            Boost <span className="opacity-70">"{listing.title}"</span>
          </h4>
        </div>
        <PulseBadge listingId={listing.id}
                    score={status?.pulse?.score}
                    band={status?.pulse?.band} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Radar */}
        <div className="border rounded-lg p-4 bg-slate-50/40">
          <div className="flex items-start gap-3 mb-2">
            <Radar className="h-5 w-5 text-[#1F3D2B]" />
            <div className="flex-1">
              <h5 className="font-medium text-[#1A1A1A]">Developments Radar</h5>
              <p className="text-xs text-slate-500 mt-1">
                Planning applications & new builds within 1km of your property.
              </p>
            </div>
          </div>
          {radarUnlocked ? (
            <Button onClick={openRadar} variant="outline" className="w-full mt-2" size="sm" data-testid={`open-radar-${listing.id}`}>
              <Unlock className="h-4 w-4 mr-2" />Open Radar
            </Button>
          ) : (
            <Button onClick={() => purchase('radar')} disabled={loadingSku === 'radar'}
                    className="w-full mt-2 bg-[#1F3D2B] text-white hover:bg-[#2A4F39]"
                    size="sm"
                    data-testid={`buy-radar-${listing.id}`}>
              {loadingSku === 'radar' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
              Unlock {prices.radar}
            </Button>
          )}
        </div>

        {/* Report */}
        <div className="border rounded-lg p-4 bg-slate-50/40">
          <div className="flex items-start gap-3 mb-2">
            <FileText className="h-5 w-5 text-[#1F3D2B]" />
            <div className="flex-1">
              <h5 className="font-medium text-[#1A1A1A]">Pulse Boost Report</h5>
              <p className="text-xs text-slate-500 mt-1">
                AI report on price tweaks, photo gaps, copy fixes to raise score.
              </p>
            </div>
          </div>
          {reportUnlocked ? (
            <Button onClick={openReport} variant="outline" className="w-full mt-2" size="sm" data-testid={`open-report-${listing.id}`}>
              <Unlock className="h-4 w-4 mr-2" />Read Report
            </Button>
          ) : (
            <Button onClick={() => purchase('report')} disabled={loadingSku === 'report'}
                    className="w-full mt-2 bg-[#1F3D2B] text-white hover:bg-[#2A4F39]"
                    size="sm"
                    data-testid={`buy-report-${listing.id}`}>
              {loadingSku === 'report' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
              Unlock {prices.report}
            </Button>
          )}
        </div>

        {/* Bundle */}
        <div className="border rounded-lg p-4 bg-gradient-to-br from-[#FEF3EC] to-white border-[#E07A3A]/30">
          <div className="flex items-start gap-3 mb-2">
            <Sparkles className="h-5 w-5 text-[#E07A3A]" />
            <div className="flex-1">
              <h5 className="font-medium text-[#1A1A1A]">Boost Bundle</h5>
              <p className="text-xs text-slate-500 mt-1">
                Both Radar + Report. Save vs buying separately.
              </p>
            </div>
          </div>
          {radarUnlocked && reportUnlocked ? (
            <div className="text-xs text-emerald-700 bg-emerald-50 px-3 py-2 rounded mt-2">Both add-ons active.</div>
          ) : (
            <Button onClick={() => purchase('bundle')} disabled={loadingSku === 'bundle'}
                    className="w-full mt-2 bg-[#E07A3A] text-white hover:bg-[#C4652E]"
                    size="sm"
                    data-testid={`buy-bundle-${listing.id}`}>
              {loadingSku === 'bundle' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Bundle {prices.bundle}
            </Button>
          )}
        </div>
      </div>

      {/* Radar drawer */}
      {radarOpen && (
        <div className="mt-4 border-t pt-4">
          <div className="flex items-center justify-between mb-2">
            <h5 className="font-medium text-[#1F3D2B]">Developments within 1km</h5>
            <button onClick={() => setRadarOpen(false)} className="text-xs text-slate-500 hover:text-slate-800">close</button>
          </div>
          {!radarData ? (
            <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>
          ) : radarData.items?.length ? (
            <ul className="space-y-2 max-h-64 overflow-y-auto">
              {radarData.items.map((it, idx) => (
                <li key={it.reference || it.url || `${idx}-${(it.title || '').slice(0, 24)}`} className="text-sm flex justify-between border-b pb-1.5">
                  <span className="text-slate-700">
                    {it.title}
                    {it.status && <span className="text-slate-400 text-xs ml-1">[{it.status}]</span>}
                  </span>
                  <span className="text-slate-400 text-xs">
                    {it.distance_km != null ? `${it.distance_km} km` : ''}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">{radarData.message || 'No nearby developments found.'}</p>
          )}
          <p className="text-[11px] text-slate-400 mt-2">{radarData?.message?.startsWith('Live') ? radarData.message : ''}</p>
        </div>
      )}

      {/* Report drawer */}
      {reportOpen && (
        <div className="mt-4 border-t pt-4">
          <div className="flex items-center justify-between mb-2">
            <h5 className="font-medium text-[#1F3D2B]">AI Pulse Boost Report</h5>
            <button onClick={() => setReportOpen(false)} className="text-xs text-slate-500 hover:text-slate-800">close</button>
          </div>
          {!reportMd ? (
            <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Generating…</div>
          ) : (
            <pre className="text-sm whitespace-pre-wrap text-slate-700 leading-relaxed font-sans bg-slate-50 p-4 rounded max-h-96 overflow-y-auto">{reportMd}</pre>
          )}
        </div>
      )}
    </div>
  );
};

export default SellerPulseBoostPanel;
